/**
 * RevenueSimulator — Formulaire + affichage résultat de simulation de revenu.
 *
 * Envoie les volumes à POST /api/v1/admin/pricing/simulate et affiche :
 *   - CA brut (EUR) + coût upstream + marge nette + % de marge
 *   - Breakdown par tier (revenu) + par provider (coût) via Recharts
 */

import { Alert, Button, Card, Form, InputNumber } from 'antd';
import { Calculator, TrendingUp } from 'lucide-react';
import { useState, type FC } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useSimulateRevenue } from '@/pages/ops/pricing/hooks/usePricingActions';
import type {
  PricingTier,
  SimulateRevenuePayload,
  UnitType,
} from '@/pages/ops/pricing/types';
import {
  PRICING_TIERS,
  TIER_LABELS,
  UNIT_TYPE_LABELS,
  UNIT_TYPES,
} from '@/pages/ops/pricing/types';

const CUSTOMER_TIERS = PRICING_TIERS.filter((t) => t !== 'any') as Exclude<
  PricingTier,
  'any'
>[];

// Unités d'usage réellement simulables (on exclut monthly_flat qui est couvert par
// customerCountByTier × platform_plan).
const USAGE_UNITS = UNIT_TYPES.filter((u) => u !== 'monthly_flat');

const BAR_COLORS = ['#3B82F6', '#6366F1', '#8B5CF6', '#A855F7', '#F59E0B', '#10B981'];

function formatEur(cents: number): string {
  const eur = cents / 100;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(eur);
}

interface FormValues {
  // Tier counts
  freeCount?: number;
  starterCount?: number;
  teamCount?: number;
  enterpriseCount?: number;
  // Usage per unit
  inputTokenAvg?: number;
  outputTokenAvg?: number;
  audioMinuteAvg?: number;
  ttsCharacterAvg?: number;
  gbMonthAvg?: number;
}

const DEFAULTS: FormValues = {
  freeCount: 100,
  starterCount: 20,
  teamCount: 5,
  enterpriseCount: 1,
  inputTokenAvg: 50_000,
  outputTokenAvg: 20_000,
  audioMinuteAvg: 120,
  ttsCharacterAvg: 50_000,
  gbMonthAvg: 5,
};

export const RevenueSimulator: FC = () => {
  const [form] = Form.useForm<FormValues>();
  const [submitted, setSubmitted] = useState(false);
  const { data, mutateAsync, isPending, error } = useSimulateRevenue();

  const handleFinish = async (values: FormValues) => {
    const customerCountByTier: SimulateRevenuePayload['customerCountByTier'] = {};
    CUSTOMER_TIERS.forEach((tier) => {
      const key = `${tier}Count` as keyof FormValues;
      const count = values[key];
      if (typeof count === 'number' && count > 0) {
        customerCountByTier[tier] = count;
      }
    });

    const avgUsageByUnit: SimulateRevenuePayload['avgUsageByUnit'] = {};
    const usageMap: Record<UnitType, keyof FormValues> = {
      input_token: 'inputTokenAvg',
      output_token: 'outputTokenAvg',
      audio_minute: 'audioMinuteAvg',
      tts_character: 'ttsCharacterAvg',
      gb_month: 'gbMonthAvg',
      monthly_flat: 'freeCount', // unused, monthly_flat handled via customerCountByTier
    };
    USAGE_UNITS.forEach((unit) => {
      const key = usageMap[unit];
      const v = values[key];
      if (typeof v === 'number' && v > 0) {
        avgUsageByUnit[unit] = v;
      }
    });

    try {
      await mutateAsync({ customerCountByTier, avgUsageByUnit });
      setSubmitted(true);
    } catch {
      // error exposé via `error`
    }
  };

  const revenueBreakdownData = data
    ? Object.entries(data.revenueBreakdownByTier).map(([tier, cents]) => ({
        name: TIER_LABELS[tier as PricingTier] ?? tier,
        value: cents / 100,
        cents,
      }))
    : [];

  const costBreakdownData = data
    ? Object.entries(data.costBreakdownByProvider).map(([provider, cents]) => ({
        name: provider,
        value: cents / 100,
        cents,
      }))
    : [];

  return (
    <div className="space-y-6">
      {/* Formulaire */}
      <Card
        title={
          <div className="flex items-center gap-2">
            <Calculator className="h-4 w-4 text-emerald-500" />
            <span>Paramètres de simulation</span>
          </div>
        }
        className="fc-card"
        styles={{ body: { padding: 20 } }}
      >
        <Form<FormValues>
          form={form}
          layout="vertical"
          initialValues={DEFAULTS}
          onFinish={handleFinish}
          autoComplete="off"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <h3 className="mb-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Nombre de clients par plan
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {CUSTOMER_TIERS.map((tier) => (
                  <Form.Item
                    key={tier}
                    label={TIER_LABELS[tier]}
                    name={`${tier}Count` as keyof FormValues}
                  >
                    <InputNumber
                      min={0}
                      step={1}
                      style={{ width: '100%' }}
                      aria-label={`Nombre clients ${TIER_LABELS[tier]}`}
                    />
                  </Form.Item>
                ))}
              </div>
            </div>

            <div>
              <h3 className="mb-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Usage moyen par unité (par client / mois)
              </h3>
              <div className="grid grid-cols-1 gap-3">
                <Form.Item label={UNIT_TYPE_LABELS.input_token} name="inputTokenAvg">
                  <InputNumber min={0} step={1000} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item label={UNIT_TYPE_LABELS.output_token} name="outputTokenAvg">
                  <InputNumber min={0} step={1000} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item label={UNIT_TYPE_LABELS.audio_minute} name="audioMinuteAvg">
                  <InputNumber min={0} step={10} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                  label={UNIT_TYPE_LABELS.tts_character}
                  name="ttsCharacterAvg"
                >
                  <InputNumber min={0} step={1000} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item label={UNIT_TYPE_LABELS.gb_month} name="gbMonthAvg">
                  <InputNumber min={0} step={1} style={{ width: '100%' }} />
                </Form.Item>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="primary"
              htmlType="submit"
              loading={isPending}
              icon={<TrendingUp className="h-4 w-4" />}
            >
              Simuler
            </Button>
          </div>
        </Form>

        {error && (
          <Alert
            className="mt-4"
            type="error"
            message="Erreur de simulation"
            description={error.message}
            showIcon
          />
        )}
      </Card>

      {/* Résultats */}
      {submitted && data && (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="fc-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Revenu brut (MRR)
              </p>
              <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
                {formatEur(data.grossRevenueCents)}
              </p>
              <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                ARR : {formatEur(data.grossRevenueCents * 12)}
              </p>
            </div>
            <div className="fc-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Coût upstream
              </p>
              <p className="mt-1 text-2xl font-bold text-red-600 dark:text-red-400">
                {formatEur(data.upstreamCostCents)}
              </p>
            </div>
            <div className="fc-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Marge nette
              </p>
              <p
                className={`mt-1 text-2xl font-bold ${
                  data.netMarginCents >= 0
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-red-600 dark:text-red-400'
                }`}
              >
                {formatEur(data.netMarginCents)}
              </p>
            </div>
            <div className="fc-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Taux de marge
              </p>
              <p
                className={`mt-1 text-2xl font-bold ${
                  data.marginPct >= 0
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-red-600 dark:text-red-400'
                }`}
              >
                {data.marginPct.toFixed(1)}%
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card
              title="Revenu par tier"
              className="fc-card"
              styles={{ body: { padding: 12 } }}
            >
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={revenueBreakdownData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" stroke="#94a3b8" tick={{ fontSize: 11 }} />
                  <YAxis
                    stroke="#94a3b8"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v: number) =>
                      new Intl.NumberFormat('fr-FR', {
                        notation: 'compact',
                      }).format(v)
                    }
                  />
                  <Tooltip
                    formatter={(value) => [
                      typeof value === 'number'
                        ? new Intl.NumberFormat('fr-FR', {
                            style: 'currency',
                            currency: 'EUR',
                            maximumFractionDigits: 0,
                          }).format(value)
                        : String(value),
                      'Revenu',
                    ]}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {revenueBreakdownData.map((_, idx) => (
                      <Cell key={idx} fill={BAR_COLORS[idx % BAR_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card
              title="Coût par provider"
              className="fc-card"
              styles={{ body: { padding: 12 } }}
            >
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={costBreakdownData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" stroke="#94a3b8" tick={{ fontSize: 10 }} />
                  <YAxis
                    stroke="#94a3b8"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v: number) =>
                      new Intl.NumberFormat('fr-FR', {
                        notation: 'compact',
                      }).format(v)
                    }
                  />
                  <Tooltip
                    formatter={(value) => [
                      typeof value === 'number'
                        ? new Intl.NumberFormat('fr-FR', {
                            style: 'currency',
                            currency: 'EUR',
                            maximumFractionDigits: 2,
                          }).format(value)
                        : String(value),
                      'Coût',
                    ]}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#EF4444" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default RevenueSimulator;
